import { today, getLocalTimeZone } from '@internationalized/date';
import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import type { ProfileLocation } from '@/api/core-api/types.gen';

import { participantProjectsControllerGetApplicationSignals } from '@/api/core-api';
import {
  EducationLevel,
  Ethnicities,
  Gender,
  HouseholdIncome,
} from '@/api/core-api/types.gen';
import { Button } from '@/components/base/buttons/button';
import { DatePicker } from '@/components/base/date-picker/date-picker';
import {
  calendarDateToISO,
  isoToCalendarDate,
} from '@/components/base/date-picker/utils';
import { FormInput } from '@/components/base/form/form-input';
import { AsyncMultiSelect } from '@/components/base/select/async-multi-select';
import { LinkedInIcon } from '@/components/foundations/linkedin-icon';
import { ProfilePictureUpload } from '@/components/profile-picture-upload/profile-picture-upload';
import { BASE_PATH } from '@/constants/config';
import {
  employmentStatusOptions,
  participantEducationOptions,
  participantEthnicityOptions,
  participantGenderOptions,
  householdIncomeOptions,
  seniorityLevelOptions,
} from '@/constants/profile-demographics-options';
import { queryKeys } from '@/hooks/api';
import { useUpdateProfile } from '@/hooks/api/use-update-profile';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { CityAutocomplete } from '@/routes/participants/-components/city-autocomplete';
import { getErrorMessage } from '@/utils/error';
import { getClient } from '@/utils/get-api-client';
import { consumeReturnTo } from '@/utils/get-safe-return-to';
import { setLinkedinVolReturnPathCookie } from '@/utils/linkedin-vol-return-cookie';
import { searchJobTitles } from '@/utils/search-job-titles';

import { LocationSchema } from './-utils/location-schema';
import {
  getOnboardingApplicationBanner,
  getOutstandingExtraSteps,
  getPrefilledOnboardingFields,
  getVisibleOnboardingSteps,
  hasRealProfilePicture,
  requiresEmployerDetails,
  type OnboardingStep,
} from './-utils/onboarding-prefilled-fields';
import { buildStepThreeSchema } from './-utils/onboarding-step-three-schema';
import { requireParticipantSession } from './-utils/participant-route-guards';

export const Route = createFileRoute('/participants/onboarding')({
  beforeLoad: ({ context, location }) => {
    const { user, profile, env } = context;

    requireParticipantSession(user, env, location);

    if (!profile) {
      throw new Error('Failed to load profile');
    }

    if (profile.isOnboardingComplete && profile.isPendingLead) {
      throw redirect({ to: '/participants/lead' });
    }

    return { profileId: profile.id, user, profile };
  },
  loader: async ({ context }) => {
    const { user, profile } = context;

    // Decides the optional trailing steps (photo, LinkedIn). Never worth
    // blocking onboarding for — any failure just hides those steps.
    const fallback = {
      hasAnyApplication: false,
      hasB2bApplication: false,
      applicationCount: 0,
      hasQualifiedApplication: false,
    };
    let applicationSignals = fallback;
    try {
      const response = await participantProjectsControllerGetApplicationSignals(
        {
          client: getClient(user.id),
          path: { userId: user.id },
        },
      );
      applicationSignals = response.data ?? fallback;
    } catch {
      applicationSignals = fallback;
    }

    // A profile past the form steps is only here to finish outstanding
    // trailing steps — with none left, the flow is done and this page
    // redirects away exactly as it always did for completed onboarding.
    if (profile.isOnboardingComplete) {
      const outstanding = getOutstandingExtraSteps(profile, {
        ...applicationSignals,
        hasPicture: hasRealProfilePicture(user.picture),
      });

      if (outstanding.length === 0) {
        throw redirect({ to: '/participants/projects' });
      }
    }

    return { applicationSignals };
  },
  component: OnboardingPage,
});

async function navigateAfterOnboardingComplete(
  queryClient: QueryClient,
  navigate: ReturnType<typeof useNavigate>,
  { isPendingLead = false }: { isPendingLead?: boolean } = {},
) {
  await queryClient.invalidateQueries({
    queryKey: queryKeys.session.current(),
  });

  if (isPendingLead) {
    void navigate({
      to: '/participants/lead',
      reloadDocument: true,
    });
    return;
  }

  const returnTo = consumeReturnTo();
  if (returnTo) {
    globalThis.location.href = returnTo;
  } else {
    void navigate({
      to: '/participants/projects',
      reloadDocument: true,
    });
  }
}

/**
 * The thanks-for-applying banner for first-time applicants who landed in
 * onboarding straight off their submission (the public apply flow redirects
 * here). The qualified variant reveals a positive; every other outcome gets
 * the same neutral copy — the banner must never confirm a negative.
 */
function ApplicationBanner({
  banner,
}: {
  banner: ReturnType<typeof getOnboardingApplicationBanner>;
}) {
  if (!banner) {
    return null;
  }

  return (
    <div
      className="flex gap-3 rounded-xl bg-success-primary p-4 shadow-xs"
      data-testid={`onboarding-application-banner-${banner}`}
    >
      {banner === 'qualified' ? (
        <span className="text-xl" role="img" aria-label="glowing star">
          🌟
        </span>
      ) : (
        <span className="text-xl" role="img" aria-label="party">
          🎉
        </span>
      )}
      <div>
        <p className="font-semibold text-primary">
          {banner === 'qualified' ? (
            <Trans>
              Thanks for your application — you&apos;re on the shortlist!
            </Trans>
          ) : (
            <Trans>Thanks for your application!</Trans>
          )}
        </p>
        <p className="mt-1 text-sm text-secondary">
          {banner === 'qualified' ? (
            <Trans>
              Finish setting up your profile below — participants with a rich
              profile are 3x more likely to get picked.
            </Trans>
          ) : (
            <Trans>
              To complete your submission, finish setting up your profile below.
              Participants with a rich profile are 3x more likely to get picked.
            </Trans>
          )}
        </p>
      </div>
    </div>
  );
}

function StepIndicator({
  position,
  total,
}: {
  position: number;
  total: number;
}) {
  const pillColor = (index: number) => {
    if (index < position) return 'bg-success-primary';
    if (index === position) return 'bg-brand-primary';
    return 'bg-tertiary';
  };
  return (
    <div
      className="flex items-center gap-2"
      aria-label={`Step ${position} of ${total}`}
    >
      {Array.from({ length: total }, (_, index) => (
        <span
          key={index}
          className={`h-2 w-8 rounded-full ${pillColor(index + 1)}`}
        />
      ))}
      <span className="ml-2 text-sm font-medium text-secondary">
        <Trans>
          Step {position} of {total}
        </Trans>
      </span>
    </div>
  );
}

function showFieldError(
  field: { state: { meta: { isBlurred: boolean; errors: unknown[] } } },
  submissionAttempts: number,
) {
  return (
    (field.state.meta.isBlurred || submissionAttempts > 0) &&
    field.state.meta.errors.length > 0
  );
}

function OnboardingPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const { profileId, env, profile, user } = Route.useRouteContext();
  const { applicationSignals } = Route.useLoaderData();

  const [serverError, setServerError] = useState<string | null>(null);

  // Fields another surface already filled in are not surfaced again; steps
  // whose fields are all prefilled are skipped entirely. A participant whose
  // form onboarding is already complete is only here for the outstanding
  // trailing steps (photo, LinkedIn) — the forms never render again.
  const [prefilled] = useState(() => getPrefilledOnboardingFields(profile));
  const [visibleSteps] = useState(() =>
    getVisibleOnboardingSteps(
      prefilled,
      profile,
      {
        ...applicationSignals,
        hasPicture: hasRealProfilePicture(user.picture),
      },
      { onboardingComplete: profile.isOnboardingComplete },
    ),
  );
  const [step, setStep] = useState<OnboardingStep>(visibleSteps[0] ?? 1);

  useEffect(() => {
    analytics?.screen({ name: 'onboarding' });
  }, [analytics]);

  const todayDate = today(getLocalTimeZone());
  const maxBirthday = todayDate.subtract({ years: 18 });
  const minBirthday = todayDate.subtract({ years: 120 });

  const updateProfileMutation = useUpdateProfile();

  // Prefilled fields are hidden, so their stored values pass through
  // unvalidated — an out-of-policy stored value must never block the step
  // behind an error the user cannot see or fix.
  const stepOneSchema = z.object({
    birthday: prefilled.birthday
      ? z.string()
      : z
          .string()
          .min(1, t`Birthday is required`)
          .refine(
            (val) => {
              const date = new Date(val);
              if (Number.isNaN(date.getTime())) return false;
              const now = new Date();
              const eighteenYearsAgo = new Date(
                now.getFullYear() - 18,
                now.getMonth(),
                now.getDate(),
              );
              return date <= eighteenYearsAgo;
            },
            { message: t`You must be at least 18 years old` },
          )
          .refine(
            (val) => {
              const date = new Date(val);
              if (Number.isNaN(date.getTime())) return false;
              const now = new Date();
              const oneHundredTwentyYearsAgo = new Date(
                now.getFullYear() - 120,
                now.getMonth(),
                now.getDate(),
              );
              return date >= oneHundredTwentyYearsAgo;
            },
            { message: t`Invalid age` },
          ),
    gender: prefilled.gender
      ? z.custom<Gender | undefined>(() => true)
      : z.enum(Gender, { message: t`Gender is required` }),
    city: prefilled.city
      ? z.custom<ProfileLocation | null>(() => true)
      : LocationSchema.refine((city) => city != null, {
          message: t`City is required`,
        }),
  });

  const stepTwoSchema = z.object({
    ethnicity: prefilled.ethnicity
      ? z.custom<Ethnicities | undefined>(() => true)
      : z.enum(Ethnicities, { message: t`Ethnicity is required` }),
    householdIncome: prefilled.householdIncome
      ? z.custom<HouseholdIncome | undefined>(() => true)
      : z.enum(HouseholdIncome, {
          message: t`Household income is required`,
        }),
    education: prefilled.education
      ? z.custom<EducationLevel | undefined>(() => true)
      : z.enum(EducationLevel, {
          message: t`Education level is required`,
        }),
  });

  const stepThreeSchema = buildStepThreeSchema(prefilled);

  const stepOneForm = useForm({
    defaultValues: {
      birthday: profile.demographics?.dateOfBirth ?? '',
      gender: profile.demographics?.gender,
      city: profile.location ?? null,
    },
    validators: { onSubmit: stepOneSchema },
    onSubmit: () => {
      goToStepAfter(1);
    },
  });

  const stepTwoForm = useForm({
    defaultValues: {
      ethnicity: profile.demographics?.ethnicity,
      householdIncome: profile.demographics?.householdIncome,
      education: profile.demographics?.educationLevel,
    },
    validators: { onSubmit: stepTwoSchema },
    onSubmit: () => {
      goToStepAfter(2);
    },
  });

  const stepThreeForm = useForm({
    defaultValues: {
      employmentStatus: profile.employment?.employmentStatus,
      companyName: profile.employment?.company ?? '',
      seniorityLevel: profile.employment?.seniorityLevel ?? undefined,
      workEmail: profile.employment?.email ?? '',
      jobTitle: profile.employment?.jobTitleId
        ? {
            id: profile.employment.jobTitleId,
            label: profile.employment.jobTitle ?? undefined,
          }
        : undefined,
    },
    validators: { onSubmit: stepThreeSchema, onChange: stepThreeSchema },
    onSubmit: () => {
      goToStepAfter(3);
    },
  });

  const finishOnboarding = async () => {
    await navigateAfterOnboardingComplete(queryClient, navigate, {
      isPendingLead: profile.isPendingLead,
    });
  };

  // Submits every step's values — visible steps use whatever the user
  // entered, skipped steps re-submit their prefilled defaults unchanged.
  // Pending leads never see step 3, so their employment is left untouched.
  // Saving is decoupled from leaving the page: when optional trailing steps
  // (photo, LinkedIn) follow the form steps, the profile is saved first and
  // `onSaved` advances into them instead of navigating away.
  const saveProfile = (onSaved: () => void | Promise<void>) => {
    setServerError(null);

    const employmentValues = stepThreeForm.state.values;
    const includeEmployment = !profile.isPendingLead;
    const isEmployed = requiresEmployerDetails(
      employmentValues.employmentStatus,
    );
    const seniorityLevel = isEmployed
      ? employmentValues.seniorityLevel
      : undefined;

    updateProfileMutation.mutate(
      {
        profileId,
        userId: user.id,
        updateProfileDto: {
          demographics: {
            gender: stepOneForm.state.values.gender,
            dateOfBirth: stepOneForm.state.values.birthday,
            ethnicity: stepTwoForm.state.values.ethnicity,
            householdIncome: stepTwoForm.state.values.householdIncome,
            educationLevel: stepTwoForm.state.values.education,
          },
          about: {
            location: stepOneForm.state.values.city ?? undefined,
          },
          ...(includeEmployment
            ? {
                employment: {
                  jobTitle: employmentValues.jobTitle?.id ?? null,
                  employmentStatus: employmentValues.employmentStatus,
                  company: isEmployed
                    ? employmentValues.companyName.trim()
                    : undefined,
                  email: isEmployed
                    ? employmentValues.workEmail.trim()
                    : undefined,
                  seniorityLevel,
                },
              }
            : {}),
        },
      },
      {
        onSuccess: async () => {
          setServerError(null);
          analytics?.track({
            action: 'completed',
            actionSubject: 'onboarding',
            attributes: {
              ageProvided: !!stepOneForm.state.values.birthday,
              genderProvided: !!stepOneForm.state.values.gender,
              locationProvided: !!stepOneForm.state.values.city,
              employmentProvided:
                includeEmployment && !!employmentValues.employmentStatus,
              seniorityProvided: includeEmployment && !!seniorityLevel,
            },
          });

          await onSaved();
        },
        onError: (error) => {
          setServerError(error.message);
        },
      },
    );
  };

  // Advance to the next step that still has fields to ask for; when the
  // current step is the last visible one, finish onboarding instead. The
  // profile is saved on leaving the last form step (≤ 3) — the trailing
  // steps only run their own side effects (photo upload, LinkedIn OAuth).
  const goToStepAfter = (current: OnboardingStep) => {
    const next = visibleSteps[visibleSteps.indexOf(current) + 1];
    const leavingFormSteps = current <= 3 && (next === undefined || next >= 4);

    if (leavingFormSteps) {
      saveProfile(async () => {
        if (next === undefined) {
          await finishOnboarding();
        } else {
          setStep(next);
        }
      });
    } else if (next === undefined) {
      void finishOnboarding();
    } else {
      setServerError(null);
      setStep(next);
    }
  };

  // The OAuth round-trip leaves this page for good — the profile is already
  // saved by now, so LinkedIn returns to onboarding's final destination
  // (this route would just redirect there anyway once complete).
  const handleConnectLinkedin = () => {
    analytics?.track(
      {
        action: 'started',
        actionSubject: 'linkedinVerification',
        attributes: { userId: user.id, source: 'onboarding' },
      },
      // location.assign() below unloads the page before the default batched
      // flush; immediate:true routes through sendBeacon so the event survives.
      { immediate: true },
    );

    const destination =
      consumeReturnTo() ?? `${BASE_PATH}/participants/projects`;
    setLinkedinVolReturnPathCookie(destination);
    globalThis.location.assign(
      `${BASE_PATH}/v4/profiles/${profileId}/linkedin-vol/authorize`,
    );
  };

  const previousStep = visibleSteps[visibleSteps.indexOf(step) - 1];
  const isLastStep = visibleSteps.indexOf(step) === visibleSteps.length - 1;

  return (
    <AuthLayout>
      <div className="flex flex-col gap-8">
        <ApplicationBanner
          banner={getOnboardingApplicationBanner(applicationSignals)}
        />
        <div className="flex flex-col gap-4">
          <div className="flex size-12 items-center justify-center rounded-full bg-brand-primary">
            {step === 3 ? (
              <span className="text-xl" role="img" aria-label="briefcase">
                💼
              </span>
            ) : step === 4 ? (
              <span className="text-xl" role="img" aria-label="camera">
                📸
              </span>
            ) : step === 5 ? (
              <span className="text-xl" role="img" aria-label="link">
                🔗
              </span>
            ) : (
              <span className="text-xl" role="img" aria-label="target">
                🎯
              </span>
            )}
          </div>
          <StepIndicator
            position={visibleSteps.indexOf(step) + 1}
            total={visibleSteps.length}
          />
          <div>
            <h1 className="font-heading text-display-xs font-semibold text-primary">
              {step === 1 ? (
                <Trans>Get targeted projects</Trans>
              ) : step === 2 ? (
                <Trans>A few more details</Trans>
              ) : step === 4 ? (
                <Trans>Add a profile photo</Trans>
              ) : step === 5 ? (
                <Trans>Connect your LinkedIn</Trans>
              ) : (
                <Trans>Tell us about your work</Trans>
              )}
            </h1>
            <p className="mt-1 text-md text-secondary">
              {step === 1 ? (
                <Trans>
                  Tell us a bit about yourself so we can match you with the best
                  research projects.
                </Trans>
              ) : step === 2 ? (
                <Trans>
                  Nearly done. This helps us send you the right studies.
                </Trans>
              ) : step === 4 ? (
                <Trans>
                  Researchers are more likely to select participants with a
                  profile photo.
                </Trans>
              ) : step === 5 ? (
                <Trans>
                  You applied to a professional study. Connecting your LinkedIn
                  verifies your work experience with researchers.
                </Trans>
              ) : (
                <Trans>
                  Verified work details unlock higher-paying professional
                  studies.
                </Trans>
              )}
            </p>
          </div>
        </div>

        {serverError && (
          <p className="text-sm text-error-primary">{serverError}</p>
        )}

        {step === 1 ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void stepOneForm.handleSubmit();
            }}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-5">
              {prefilled.birthday ? null : (
                <stepOneForm.Field
                  name="birthday"
                  children={(field) => {
                    const errors = field.state.meta.errors;
                    const showError = showFieldError(
                      field,
                      stepOneForm.state.submissionAttempts,
                    );
                    return (
                      <DatePicker
                        label={t`Age (18+)`}
                        data-testid="participant-onboarding-birthday-input"
                        value={
                          field.state.value
                            ? isoToCalendarDate(field.state.value)
                            : null
                        }
                        onChange={(date) => {
                          field.handleChange(
                            date ? calendarDateToISO(date) : '',
                          );
                        }}
                        onBlur={field.handleBlur}
                        maxValue={maxBirthday}
                        minValue={minBirthday}
                        isRequired
                        hideRequiredIndicator
                        isInvalid={showError}
                        hint={
                          showError ? getErrorMessage(errors[0]) : undefined
                        }
                      />
                    );
                  }}
                />
              )}

              {prefilled.gender ? null : (
                <div data-testid="participant-onboarding-gender-input">
                  <FormInput
                    type="select"
                    field="gender"
                    size="md"
                    form={stepOneForm}
                    label={t`Gender`}
                    placeholder={t`Select gender`}
                    items={participantGenderOptions}
                  />
                </div>
              )}

              {prefilled.city ? null : (
                <stepOneForm.Field
                  name="city"
                  children={(field) => {
                    const errors = field.state.meta.errors;
                    const showError = showFieldError(
                      field,
                      stepOneForm.state.submissionAttempts,
                    );
                    return (
                      <div data-testid="participant-onboarding-city-input">
                        <CityAutocomplete
                          value={field.state.value}
                          onChange={(v) => {
                            field.handleChange(v);
                          }}
                          onBlur={field.handleBlur}
                          isInvalid={showError}
                          hint={
                            showError ? getErrorMessage(errors[0]) : undefined
                          }
                          apiKey={env.GOOGLE_BROWSER_APIKEY}
                        />
                      </div>
                    );
                  }}
                />
              )}
            </div>

            <Button
              type="submit"
              color="primary"
              size="lg"
              className="w-full"
              data-testid="participant-onboarding-continue-button"
              isLoading={updateProfileMutation.isPending}
            >
              {isLastStep ? <Trans>Finish</Trans> : <Trans>Continue</Trans>}
            </Button>
          </form>
        ) : step === 2 ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void stepTwoForm.handleSubmit();
            }}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-5">
              {prefilled.ethnicity ? null : (
                <div data-testid="participant-onboarding-ethnicity-input">
                  <FormInput
                    type="select"
                    field="ethnicity"
                    size="md"
                    form={stepTwoForm}
                    label={t`Ethnicity`}
                    placeholder={t`Select ethnicity`}
                    items={participantEthnicityOptions}
                  />
                </div>
              )}

              {prefilled.householdIncome ? null : (
                <div data-testid="participant-onboarding-household-income-input">
                  <FormInput
                    type="select"
                    field="householdIncome"
                    size="md"
                    form={stepTwoForm}
                    label={t`Household income`}
                    placeholder={t`Select household income`}
                    items={householdIncomeOptions}
                  />
                </div>
              )}

              {prefilled.education ? null : (
                <div data-testid="participant-onboarding-education-input">
                  <FormInput
                    type="select"
                    field="education"
                    size="md"
                    form={stepTwoForm}
                    label={t`Education level`}
                    placeholder={t`Select education level`}
                    items={participantEducationOptions}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Button
                type="submit"
                color="primary"
                size="lg"
                className="w-full"
                data-testid="participant-onboarding-continue-button"
                isLoading={updateProfileMutation.isPending}
              >
                {isLastStep ? <Trans>Finish</Trans> : <Trans>Continue</Trans>}
              </Button>
              {previousStep === undefined ? null : (
                <Button
                  type="button"
                  color="secondary"
                  size="lg"
                  className="w-full"
                  data-testid="participant-onboarding-back-button"
                  onClick={() => setStep(previousStep)}
                >
                  <Trans>Back</Trans>
                </Button>
              )}
            </div>
          </form>
        ) : step === 3 ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void stepThreeForm.handleSubmit();
            }}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-5">
              {prefilled.employmentStatus ? null : (
                <div data-testid="participant-onboarding-employment-status-input">
                  <FormInput
                    type="select"
                    field="employmentStatus"
                    size="md"
                    form={stepThreeForm}
                    label={t`Employment status`}
                    placeholder={t`Select employment status`}
                    items={employmentStatusOptions}
                  />
                </div>
              )}

              <stepThreeForm.Subscribe
                selector={(state) =>
                  requiresEmployerDetails(state.values.employmentStatus)
                }
                children={(showEmployerFields) => (
                  <div
                    className={`flex flex-col gap-5${showEmployerFields ? '' : ' hidden'}`}
                  >
                    {prefilled.companyName ? null : (
                      <div data-testid="participant-onboarding-company-name-input">
                        <FormInput
                          type="text"
                          field="companyName"
                          size="md"
                          form={stepThreeForm}
                          label={t`Company name`}
                          placeholder={t`Company name`}
                        />
                      </div>
                    )}
                    {prefilled.jobTitle ? null : (
                      <stepThreeForm.Field
                        name="jobTitle"
                        children={(field) => (
                          <AsyncMultiSelect
                            label={t`Job title`}
                            value={field.state.value ? [field.state.value] : []}
                            onChange={(items) => {
                              const next = items[0];
                              field.setValue(
                                next
                                  ? { id: next.id, label: next.label ?? '' }
                                  : undefined,
                              );
                            }}
                            onSearch={searchJobTitles}
                            placeholder={t`Search job titles...`}
                            maxSelections={1}
                            minSearchChars={0}
                            size="md"
                          />
                        )}
                      />
                    )}
                    {prefilled.seniorityLevel ? null : (
                      <div data-testid="participant-onboarding-seniority-level-input">
                        <FormInput
                          type="select"
                          field="seniorityLevel"
                          size="md"
                          form={stepThreeForm}
                          label={t`Seniority level`}
                          placeholder={t`Select seniority level`}
                          items={seniorityLevelOptions}
                        />
                      </div>
                    )}
                    {prefilled.workEmail ? null : (
                      <div data-testid="participant-onboarding-work-email-input">
                        <FormInput
                          type="email"
                          field="workEmail"
                          size="md"
                          form={stepThreeForm}
                          label={t`Work email`}
                          placeholder={t`name@company.com`}
                          hint={t`We'll send one email to this address to verify it, and never anything else. Study invitations still go to your personal email.`}
                        />
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col gap-3">
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={updateProfileMutation.isPending}
                isDisabled={updateProfileMutation.isPending}
                className="w-full"
                data-testid="participant-onboarding-submit-button"
              >
                {isLastStep ? <Trans>Finish</Trans> : <Trans>Continue</Trans>}
              </Button>
              {previousStep === undefined ? null : (
                <Button
                  type="button"
                  color="secondary"
                  size="lg"
                  className="w-full"
                  data-testid="participant-onboarding-back-button"
                  onClick={() => setStep(previousStep)}
                >
                  <Trans>Back</Trans>
                </Button>
              )}
            </div>
          </form>
        ) : step === 4 ? (
          <div data-testid="participant-onboarding-picture-step">
            <ProfilePictureUpload
              userId={user.id}
              hideHeading
              cancelLabel={<Trans>Skip for now</Trans>}
              onUploadSuccess={() => {
                goToStepAfter(4);
              }}
              onCancel={() => {
                goToStepAfter(4);
              }}
            />
          </div>
        ) : (
          <div
            className="flex flex-col gap-6"
            data-testid="participant-onboarding-linkedin-step"
          >
            <div className="flex flex-row items-start gap-3 rounded-2xl border border-secondary bg-primary p-6 shadow-xs">
              <div className="overflow-hidden rounded-full bg-[#0077B5] p-1">
                <LinkedInIcon className="z-10 size-10 rounded-full" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <h2 className="font-semibold text-primary">
                  <Trans>LinkedIn</Trans>
                </h2>
                <p className="text-sm text-secondary">
                  <Trans>
                    We&apos;ll only use your LinkedIn to verify your work
                    experience and won&apos;t contact you through it.
                  </Trans>
                </p>
              </div>
            </div>
            <Button
              type="button"
              color="primary"
              size="lg"
              className="w-full"
              iconLeading={<LinkedInIcon className="size-4" />}
              data-testid="participant-onboarding-linkedin-button"
              onClick={handleConnectLinkedin}
            >
              <Trans>Connect LinkedIn</Trans>
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
